import * as React from 'react';
import type { OnboardingDocumentRequirement, OnboardingDocumentRequirementResult } from '@gcv/shared';
import { RequirementCategory } from '@gcv/shared';
import { useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';

import { useComponent } from 'hooks';
import { useInjection } from 'ioc';
import { OnboardingPresenter } from 'ui/apps/crb/onboarding-two/onboarding.presenter';
import { theme } from 'ui/theme';
import { CommentAddedBox } from './comment-added-box';
import { FileUploadedBox } from './file-uploaded-box';
import { MultipleFilesUploadedBox } from './multiple-files-uploaded-box';
import { UploadFileButton } from './upload-file-button';
import { UploadLicenseButton } from './upload-license-button';

interface Props extends Record<string, unknown> {
  requirement: OnboardingDocumentRequirement;
  result?: OnboardingDocumentRequirementResult;
}

export const RequirementActionsContainer: React.FC<Props> = useComponent(({ requirement, result }) => {
  const presenter = useInjection(OnboardingPresenter);
  const [viewAll, setViewAll] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const doesRequirementHaveComment = () => {
    return result?.do_not_have_comments && result?.do_not_have && !doesRequirementHaveDocuments();
  };
  const doesRequirementHaveDocuments = () => {
    return result?.documents && Object.keys(result.documents).length > 0;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isMobile || isTablet ? 'center' : 'flex-start',
        marginBottom: 'auto'
      }}
    >
      {doesRequirementHaveComment() && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
          <CommentAddedBox requirement={requirement} result={result} />
        </Box>
      )}

      {doesRequirementHaveDocuments() && (viewAll || Object.keys(result!.documents).length === 1) ? (
        <Box
          sx={{
            border: '1px solid #C6CCDA',
            borderRadius: '6px',
            padding: isMobile ? '0' : '0px 8px',
            maxWidth: '300px',
            minWidth: isMobile ? 'unset' : '300px',
            marginX: 'auto'
          }}
        >
          <Box data-cy="files-uploaded-container">
            {Object.values(result!.documents).map((doc, index) => {
              return (
                <Box
                  sx={{
                    borderBottom:
                      index !== Object.values(result!.documents).length - 1 ? '1px solid #C6CCDA' : ''
                  }}
                >
                  <FileUploadedBox
                    requirement={requirement}
                    result={result}
                    documentUpload={presenter.viewModel.allDocumentsMap[doc.id]}
                    documentId={doc.id}
                    key={doc.id}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {doesRequirementHaveDocuments() &&
      !viewAll &&
      result?.documents &&
      Object.keys(result.documents).length > 1 ? (
        <Box sx={{ width: '100%', maxWidth: '300px', marginX: 'auto' }}>
          <MultipleFilesUploadedBox setViewAll={() => setViewAll(true)} />
        </Box>
      ) : null}

      {!doesRequirementHaveComment() ? (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {requirement.category !== RequirementCategory.License ? (
            <UploadFileButton requirement={requirement} result={result} />
          ) : (
            <UploadLicenseButton requirement={requirement} result={result} />
          )}
        </Box>
      ) : null}
    </Box>
  );
});
